/**
 * TanStack Query hooks for API key management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiKeysApi } from '../api/apiKeys';

// ============================================================================
// Query Keys
// ============================================================================

export const apiKeyKeys = {
  all: ['api-keys'] as const,
  list: () => [...apiKeyKeys.all, 'list'] as const,
};

// ============================================================================
// Hooks
// ============================================================================

/** List all API keys for the current user */
export function useApiKeys() {
  return useQuery({
    queryKey: apiKeyKeys.list(),
    queryFn: () => apiKeysApi.list(),
  });
}

/** Create a new API key */
export function useCreateApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => apiKeysApi.create(name),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: apiKeyKeys.all });
    },
  });
}

/** Revoke (soft-disable) an API key */
export function useRevokeApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiKeysApi.revoke(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: apiKeyKeys.all });
    },
  });
}

/** Rotate a key — the response carries the NEW full key (shown once) */
export function useRotateApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiKeysApi.rotate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: apiKeyKeys.all });
    },
  });
}

// ponytail: a mutation, not a query — a secret must never land in the query cache.
/** Reveal the full key for a row */
export function useRevealApiKey() {
  return useMutation({
    mutationFn: (id: number) => apiKeysApi.reveal(id),
  });
}

/** Permanently delete an API key */
export function useDeleteApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiKeysApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: apiKeyKeys.all });
    },
  });
}
